import { useStoreState } from "pullstate";
import { createContext, useEffect, useState, useRef } from "react";
import Store, { IStore } from "../../utils/Store";
import { clone, cloneDeep, get, isEmpty, set } from "lodash";
import { Patient } from "../../types/api/Patient";
import { PatientsResponse } from "../../types/api/Patients";
import useWebsocket from "../../hooks/useWebsocket";
import useMedia, { MediaSizes } from "../../hooks/useMedia";
import constants from "../../utils/constants";
import { getIsOnpremise, isValidDate } from "../../utils";
import { useNetworkStatus } from "../../hooks/useNetworkStatus";
import getPatients from "./services/getPatients";
import getCounts from "./services/getCounts";
import getSyncStatus from "./services/getSyncStatus";
import { getEmergencyPatients } from "../WorklistTagsSetting/services";
import { add } from "date-fns";
import getMatchingPatient from "../MatchingPatient/services/getMatchingPatient";
import { downloadFile } from "../ExportData/services/downloadService";
import { useRouter } from "next/router";
import useCheckWorklistContainer from "../../hooks/useCheckWorklistContainer";
import getPatientDetails from "./services/getPatientDetails";
import Toast from "../Toast";
import getEnv from "../../utils/getEnv";
import getCloudSyncStatus, { PayloadSuccess } from "./services/getCloudSyncStatus";
import { useAuth } from "@qure/qure-auth";
import { PendingReferral } from "../WorkspaceManagement/types";
import useGetUsersTags from "../CustomTagsManagement/services/useGetUsersTags";
import { CustomFieldObj } from "../CustomTagsManagement/types/custom-fields";
import { trackQsyncInitiated } from "../../utils/mixpanel";

export const PatientsContext = createContext<any>({});

export const PatientsContextProvider = (props: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { children } = props;
  const pageSize = constants.PAGE_SIZE;
  const {
    newPatientId,
    worklistCloudSyncStatus,
    lastGoogleCheckStatus,
    hasQsyncDataFromOnline,
    appConfig,
  } = useStoreState(Store, (s: IStore) => ({
    newPatientId: s.newPatientId,
    worklistCloudSyncStatus: s.worklistCloudSyncStatus,
    lastGoogleCheckStatus: s.lastGoogleCheckStatus,
    hasQsyncDataFromOnline: s.hasQsyncDataFromOnline,
    appConfig: s.appConfig,
  }));
  const { userInfo, refetchProfile }: any = useAuth();
  const userId = get(userInfo, ["username"], "");
  const isLoggedIn = get(userInfo, ["authenticated"], false);
  const [lastId, setLastId] = useState("");
  const [patientsData, setPatientsData] = useState<PatientsResponse | null>(null);
  const [updatedMessage, setUpdatedMessage] = useState({});
  const [unreadMessages, setUnreadMessages] = useState({});
  const [currentPatient, setCurrentPatient] = useState<Partial<Patient>>({});
  const [isPatientsLoading, setPatientsLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [emergencyPatients, setEmergencyPatients] = useState<Partial<Patient>[]>([]);
  // const [unsyncCount, setUnsyncCount] = useState(0);
  const [isCountLoading, setIsCountLoading] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<any>(null);
  const [pendingReferral, setPendingReferral] = useState<PendingReferral[]>(
    get(userInfo, ["pending_referrals"], [])
  );
  const [pendingRequest, setPendingRequest] = useState<PendingReferral[]>(
    get(userInfo, ["pending_requests"], [])
  );
  const _persistedFilters = localStorage?.getItem(constants.PERSISTED.filterObj);
  const [filters, setFilters] = useState(
    _persistedFilters ? JSON.parse(_persistedFilters) : constants.DEEFAULT_FILTER
  );
  const _persistedSavedFilters = localStorage?.getItem(
    constants.PERSISTED.savedFilterData
  );
  const [savedFilterData, setSavedFilterData] = useState(
    _persistedSavedFilters ? JSON.parse(_persistedSavedFilters) : null
  );
  const _persistedFullWidthWorklist = localStorage?.getItem(
    constants.PERSISTED.isFullWidthWorklist
  );
  const [isFullWidthWorklist, setIsFullWidthWorklist] = useState(
    !!_persistedFullWidthWorklist
  );
  const [matchingPatients, setMatchingPatients] = useState([]);
  const syncTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isCustomTagsEnabled = get(appConfig, ["custom_tags_filter"], false);
  const { data: userCustomFields, refetch: refetchUserCustomFields } =
    useGetUsersTags(isCustomTagsEnabled);

  const { socket, wsData } = useWebsocket(`/worklist/${userId}/`, isLoggedIn);
  const router = useRouter();
  const isMobile = useMedia([MediaSizes.MOBILE], [true], false);
  const isOnPremise = getIsOnpremise();
  const isOnline = useNetworkStatus();
  const isFormModalOpen =
    getEnv("NEXT_PUBLIC_OPEN_FORM_MODAL_AFTER_REGISTRATION") === "true";
  const currentPatientUid = get(currentPatient, ["patient_uid"], 0);
  const isWorkList = useCheckWorklistContainer();

  const patientSourceId = get(currentPatient, ["source_id"], "");
  const patientSyncStatus = worklistCloudSyncStatus[patientSourceId];
  const hasSyncEnabled =
    get(patientSyncStatus, ["payload", "kind"], "")?.toLocaleLowerCase() === "success";
  const hasFileSyncEnabled = get(
    patientSyncStatus,
    ["payload", "file_sync_enabled"],
    false
  );

  const getPatientData = () => {
    const filterParams = getFilterParams(filters);
    const params: any = { page_size: pageSize, ...filterParams };
    if (lastId) {
      params.pagination_id = lastId;
    }
    setPatientsLoading(true);
    getPatients(params, patientsData)
      .then(async (_patientsData) => {
        let _emergencyPatients = [];
        if (!lastId) {
          const _emergencyPatientsData = await getEmergencyPatients();
          _emergencyPatients = get(_emergencyPatientsData, ["data", "content"], []);
          setEmergencyPatients(_emergencyPatients);
          if (filters?.isFilterApplied && !isMobile && isWorkList) {
            if (!!_patientsData?.data?.content?.length) {
              router.push(`/patients/${_patientsData?.data?.content[0]?.patient_uid}`);
              setCurrentPatient(_patientsData?.data?.content[0]);
            } else {
              router.push(`/patients/`);
              setCurrentPatient({});
            }
          }
        } else {
          _emergencyPatients = emergencyPatients;
        }

        const _updatedPatients = updatePatientList(
          _patientsData || patientsData,
          _emergencyPatients
        );
        setPatientsData(_updatedPatients);
        setPatientsLoading(false);
      })
      .catch(() => {
        setPatientsLoading(false);
      });
  };

  const getPatientCount = () => {
    const params: any = getFilterParams(filters);
    setIsCountLoading(true);
    getCounts(params)
      .then((_count) => {
        setCount(_count);
        setIsCountLoading(false);
      })
      .catch(() => {
        setCount(0);
        setIsCountLoading(false);
      });
  };

  useEffect(() => {
    if (!isEmpty(userInfo) && isLoggedIn) {
      setPendingReferral(userInfo?.pending_referrals);
      setPendingRequest(userInfo?.pending_requests);
    }
  }, [userInfo, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      getPatientData();
    }
  }, [lastId, pageSize, filters]);

  useEffect(() => {
    /* get total patient count just for display on top on worklist */
    if (isLoggedIn) {
      getPatientCount();
    }
  }, [filters]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    if (isOnPremise) {
      if (!isMobile) {
        setIsFullWidthWorklist(true);
      }

      // Only skip if Google is online AND we already have fresh data
      if (isOnline && hasQsyncDataFromOnline && lastGoogleCheckStatus === "online") {
        // Skip redundant call, we already have fresh data
        return;
      }

      // Call QSYNC on mount unless we have fresh data from online state
      getSyncStatus()
        .then((resp) => {
          Store.update((s: IStore) => {
            s.worklistSyncStatus = resp;
            if (isOnline) {
              s.hasQsyncDataFromOnline = true;
              s.lastGoogleCheckStatus = "online";
              // Track qSync initiated event
              trackQsyncInitiated();
            }
          });
        })
        .catch((error) => {
          console.error("Failed to fetch sync status:", error);
          Store.update((s: IStore) => {
            s.worklistSyncStatus = {
              ...s.worklistSyncStatus,
              status: "NO_INTERNET",
            };
            if (isOnline) {
              s.hasQsyncDataFromOnline = false; // Allow retry
            }
          });
        });
    } else {
      getCloudSyncStatus().then((resp) => {
        Store.update((s: IStore) => {
          s.worklistCloudSyncStatus = resp;
        });
      });
    }
  }, [isOnPremise, isLoggedIn, isMobile]);

  // Network detection for OnPremise devices with 20-second delay
  useEffect(() => {
    if (!isOnPremise || !isLoggedIn) return;
    const QSYNC_DELAY = 20000;

    // ALWAYS clear any existing timer first to prevent multiple timers
    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current);
      syncTimerRef.current = null;
    }

    if (!isOnline) {
      // Google is not accessible
      if (lastGoogleCheckStatus === null || lastGoogleCheckStatus !== "offline") {
        // First time offline (from null or from online)
        Store.update((s: IStore) => {
          s.lastGoogleCheckStatus = "offline";
        });

        // Wait 20 seconds before calling QSYNC API
        syncTimerRef.current = setTimeout(() => {
          syncTimerRef.current = null; // Clear ref after execution
          // Call QSYNC API even when offline (might work on local network)
          getSyncStatus()
            .then((resp) => {
              // Only update from API response - no optimistic updates
              Store.update((s: IStore) => {
                s.worklistSyncStatus = resp;
              });
            })
            .catch((error) => {
              console.error("QSYNC API failed while offline:", error);
              // Don't set any status - only update from successful API response
            });
        }, QSYNC_DELAY);
      }
      localStorage.removeItem(constants.PERSISTED.qSyncEventLogged);
      // If already offline, don't keep calling QSYNC
    } else {
      // Google is accessible
      if (
        lastGoogleCheckStatus === null ||
        lastGoogleCheckStatus !== "online" ||
        !hasQsyncDataFromOnline
      ) {
        // First time online OR we don't have fresh data from online state
        Store.update((s: IStore) => {
          s.lastGoogleCheckStatus = "online";
        });

        // Wait 20 seconds before calling QSYNC API
        syncTimerRef.current = setTimeout(() => {
          syncTimerRef.current = null; // Clear ref after execution
          getSyncStatus()
            .then((resp) => {
              // Only update from API response - no optimistic updates
              Store.update((s: IStore) => {
                s.worklistSyncStatus = resp;
                s.hasQsyncDataFromOnline = true; // Mark that we have fresh data
              });
              // Track qSync initiated event
              trackQsyncInitiated();
            })
            .catch((error) => {
              console.error("QSYNC API failed while online:", error);
              Store.update((s: IStore) => {
                s.hasQsyncDataFromOnline = false; // Allow retry on next check
              });
              // Don't set any status - only update from successful API response
            });
        }, QSYNC_DELAY);
      }
      // If Google remains accessible and we have fresh data, don't call QSYNC
    }

    // Cleanup timer on unmount or dependency change
    return () => {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
        syncTimerRef.current = null;
      }
    };
  }, [isOnline, isOnPremise, isLoggedIn]);

  // useEffect(() => {
  //   if (isOnPremise && isFullWidthWorklist) {
  //     getUnsyncCount().then((unsync_count) => {
  //       setUnsyncCount(unsync_count);
  //     });
  //   }
  // }, [isOnPremise, isFullWidthWorklist]);

  useEffect(() => {
    if (!isEmpty(currentPatient)) {
      const is_license_expired = get(currentPatient, ["is_license_expired"], false);
      findMatchingPatient(currentPatientUid);
      Store.update((s: IStore) => {
        s.hasLicenseExpiredForSelectedPatient = is_license_expired;
      });
    }
  }, [currentPatientUid]);

  const getFilterParams = (_filters: typeof filters) => {
    const filterParams = {
      ...(_filters?.data ?? {}),
    };
    let startDate = get(filterParams, ["start_study_date"], "");
    let endDate = get(filterParams, ["end_study_date"], "");

    if (startDate) {
      let startDateVal = new Date(startDate);
      let endDateVal = add(new Date(endDate), { days: 1 });

      if (isValidDate(startDateVal) && isValidDate(endDateVal)) {
        startDateVal.setHours(0, 0, 0);
        endDateVal.setHours(0, 0, 0);
        set(filterParams, ["start_study_date"], startDateVal.toISOString());
        set(filterParams, ["end_study_date"], endDateVal.toISOString());
      }
    }
    // custom tags copy to avoid mutating the original array
    const custom_tags_copy: { value: string; logic: string; tag_id: number[] }[] = [];
    filterParams?.custom_tags?.forEach(
      (field: { tag_id: number; value: string; logic: string }) => {
        const matchingTag = userCustomFields?.find((f) => f.id === field.tag_id);
        const newField: { value: string; logic: string; tag_id: number[] } = {
          value: field.value,
          logic: field.logic,
          tag_id: [],
        };
        if (matchingTag) {
          const customFieldsIds: number[] =
            userCustomFields?.reduce((acc: number[], field: CustomFieldObj) => {
              if (
                field.tag === matchingTag.tag &&
                field.tag_type === matchingTag.tag_type
              ) {
                acc.push(field.id);
              }
              return acc;
            }, []) || [];
          newField.tag_id = customFieldsIds as number[];
        }
        custom_tags_copy.push(newField);
      }
    );
    set(filterParams, ["custom_tags"], custom_tags_copy);
    return filterParams;
  };

  const updatePatientUnreadCount = (
    currentPatientUid: number,
    hasNewMessage?: boolean
  ) => {
    const patients = get(patientsData, ["data", "content"], []);
    const currentPatient =
      patients && patients.find((p) => get(p, ["patient_uid"], 0) == currentPatientUid);
    let count = 0;
    if (hasNewMessage) {
      count = get(currentPatient, ["unread_message_count"], 0) + 1;
    }
    if (currentPatient) {
      set(currentPatient, ["unread_message_count"], count);
      if (!isOnPremise) {
        set(
          currentPatient,
          ["sync_status", "cloud", "status"],
          constants.SYNC_STATUS.CLOUD.READ
        );
      }
    }
  };

  const getPatientsByEmergency = (_patients: Partial<Patient>[]) => {
    const _emergencyPatients: Partial<Patient>[] = [];
    const _nonEmergencyPatients: Partial<Patient>[] = [];
    _patients.forEach((_pat) => {
      if (get(_pat, ["emergency"], false) === true) {
        _emergencyPatients.push(_pat);
      } else {
        _nonEmergencyPatients.push(_pat);
      }
    });

    return {
      _emergencyPatients,
      _nonEmergencyPatients,
    };
  };

  const formPatientStateObject = (
    _patients: Partial<Patient>[],
    _patientsData: PatientsResponse | null
  ) => {
    if (_patientsData) {
      const content = Object.assign(_patientsData, {
        data: { ..._patientsData.data, content: _patients },
      });
      return content;
    }
    return _patientsData;
  };

  const updatePatientList = (
    patientsData: PatientsResponse | null,
    _emergencyPatients: Partial<Patient>[]
  ) => {
    const patientIds = _emergencyPatients.map((_pat) => _pat.patient_uid);
    const _patientsData = clone(patientsData);
    let _patients = get(_patientsData, ["data", "content"], []);
    _patients = _patients.filter((_pat) => !patientIds.includes(_pat.patient_uid));
    _patients = [..._emergencyPatients, ..._patients];
    return formPatientStateObject(_patients, _patientsData);
  };

  const findMatchingPatient: any = async (_patientUid: number) => {
    if (_patientUid) {
      const matches: any = await getMatchingPatient(_patientUid);
      setMatchingPatients(matches);
    }
  };

  const relocateEmergencyPatient = (
    _patients: Partial<Patient>[],
    _targetPatient: Partial<Patient>
  ) => {
    let _allPatientsList = _patients.filter(
      (pat) => pat.patient_uid != _targetPatient?.patient_uid
    );
    let { _emergencyPatients, _nonEmergencyPatients } =
      getPatientsByEmergency(_allPatientsList);
    const hasEmergencyActivated = _targetPatient.emergency;

    if (hasEmergencyActivated) {
      // when emergency is activated
      const nextPatientIndex = _emergencyPatients.findIndex(
        (_pat) =>
          _pat.last_image_study_uploaded_at &&
          _targetPatient.last_image_study_uploaded_at &&
          new Date(_pat.last_image_study_uploaded_at) <
            new Date(_targetPatient.last_image_study_uploaded_at)
      );
      if (nextPatientIndex >= 0) {
        _emergencyPatients = [
          ..._emergencyPatients.slice(0, nextPatientIndex),
          _targetPatient,
          ..._emergencyPatients.slice(nextPatientIndex),
        ];
      } else {
        _emergencyPatients.push(_targetPatient);
      }
    } else {
      // when emergency is deactivated
      const nextPatientIndex = _nonEmergencyPatients.findIndex(
        (_pat) =>
          _pat.last_image_study_uploaded_at &&
          _targetPatient.last_image_study_uploaded_at &&
          new Date(_pat.last_image_study_uploaded_at) <
            new Date(_targetPatient.last_image_study_uploaded_at)
      );
      if (nextPatientIndex >= 0) {
        _nonEmergencyPatients = [
          ..._nonEmergencyPatients.slice(0, nextPatientIndex),
          _targetPatient,
          ..._nonEmergencyPatients.slice(nextPatientIndex),
        ];
      } else if (count <= 10) {
        _nonEmergencyPatients.push(_targetPatient);
      }
    }
    _allPatientsList = [..._emergencyPatients, ..._nonEmergencyPatients];

    return { _allPatientsList, _emergencyPatients, _nonEmergencyPatients };
  };

  useEffect(() => {
    if (wsData) {
      const filterObj = localStorage.getItem(constants.PERSISTED.filterObj);
      let isFilterApplied = false;
      if (filterObj) {
        isFilterApplied = JSON.parse(filterObj)?.isFilterApplied;
      }

      if (
        wsData.action == "new_message" ||
        wsData.action == "update_message" ||
        wsData.action == "unsubscribe" ||
        wsData.action == "subscribe"
      ) {
        const _unreadMessages = clone(unreadMessages);
        if (wsData.action == "new_message") {
          const msgContent = get(wsData, ["content"], {});
          const msgPatientUid = get(msgContent, ["patient_uid"], 0);
          // --- Kept this code for future use ---
          // const msgsList = _unreadMessages[msgPatientUid] || []
          // msgsList.unshift(msgContent)
          set(_unreadMessages, [msgPatientUid], [msgContent]); // [...msgsList]
          if (
            get(msgContent, ["user"], "") !== userId &&
            msgPatientUid != currentPatientUid
          ) {
            updatePatientUnreadCount(msgPatientUid, true);
          }
          if (
            (get(msgContent, ["image_report_uid"], 0) > 0 ||
              get(msgContent, ["image_study_uid"], 0) > 0) &&
            msgPatientUid === currentPatientUid &&
            isWorkList
          ) {
            Store.update((s: IStore) => {
              s.enableViewerAPICall = false;
              s.enableSidebarAPICall = false;
              s.hasUpdatedStudy = true;
            });
            setTimeout(() => {
              Store.update((s: IStore) => {
                s.enableSidebarAPICall = true;
              });
            }, 500);
          }
        }
        if (wsData.action == "update_message") {
          const msgContent = get(wsData, ["content"], {});
          setUpdatedMessage(msgContent);
        }
        if (wsData.action == "unsubscribe") {
          const msgPatientUid = get(wsData, ["patient_uid"], 0);
          set(_unreadMessages, [msgPatientUid], []);
        }
        if (wsData.action == "subscribe") {
          const msgPatientUid = get(wsData, ["patient_uid"], 0);

          updatePatientUnreadCount(msgPatientUid);
        }
        setUnreadMessages(_unreadMessages);
      }

      if (wsData.action == "new_patient") {
        const newPatient = get(wsData, ["content"], {});
        const _newPatientId = get(newPatient, ["patient_id"], "");
        const _newPatientUid = get(newPatient, ["patient_uid"], "");
        const _patientsData: PatientsResponse | null = clone(patientsData);
        const _patients = get(patientsData, ["data", "content"], []);
        const { _emergencyPatients, _nonEmergencyPatients } =
          getPatientsByEmergency(_patients);

        const updatePatientList = () => {
          _nonEmergencyPatients.unshift(newPatient);
          const updatedPatientData = formPatientStateObject(
            [..._emergencyPatients, ..._nonEmergencyPatients],
            _patientsData
          );
          setPatientsData(updatedPatientData);
          setCount(count + 1);
        };

        // If filter is applied then check if new patient should be added at the top of worklist
        if (isFilterApplied) {
          const filterParams = getFilterParams(filters);
          const params: any = {
            page_size: pageSize,
            ...filterParams,
            patient_pk: _newPatientUid,
          };
          getPatients(params).then((_patientsData) => {
            let _filteredPatient = get(_patientsData, ["data", "content"], []);
            if (_filteredPatient && _filteredPatient.length) {
              updatePatientList();
            }
          });
        } else {
          updatePatientList();
        }

        // for auto selecting newly registered patient by user
        if (newPatientId && _newPatientId && newPatientId === _newPatientId) {
          if (isMobile) {
            const workList = document.getElementById("worklist-container");
            if (workList) {
              workList["scrollTop"] = 0;
            }
          } else {
            router.push(`/patients/${_newPatientUid}`).then(() => {
              Store.update((s: IStore) => {
                s.isFormSelectorVisible = isFormModalOpen;
              });
            });
          }
          Store.update((s: IStore) => {
            s.newPatientId = "";
          });
        }
      }

      if (wsData.action === "new_emergency" || wsData.action === "update_emergency") {
        const newPatientEmergencyContent = get(wsData, ["content"], {});
        const patientId = get(newPatientEmergencyContent, ["patient_id"]);
        const stale = get(newPatientEmergencyContent, ["stale"], false);
        const _patientsData = clone(patientsData);
        let _patients: Partial<Patient>[] = get(_patientsData, ["data", "content"], []);
        // let _emergencyPatients: Partial<Patient>[] = clone(emergencyPatients);

        if (wsData.action === "update_emergency") {
          let _selectedEmergencyPatient = _patients.find(
            (_pat) => _pat.patient_uid === patientId
          );

          if (_selectedEmergencyPatient) {
            _selectedEmergencyPatient.emergency = !stale;
            _selectedEmergencyPatient.emergency_details = stale
              ? null
              : newPatientEmergencyContent;

            if (stale) {
              const { _allPatientsList, _emergencyPatients } = relocateEmergencyPatient(
                _patients,
                _selectedEmergencyPatient
              );
              _patients = _allPatientsList;
              setEmergencyPatients(_emergencyPatients);
            }
          }
          if (
            currentPatient &&
            _selectedEmergencyPatient &&
            currentPatient?.patient_uid === _selectedEmergencyPatient.patient_uid
          ) {
            setCurrentPatient({ ..._selectedEmergencyPatient });
          }
        } else if (wsData.action === "new_emergency") {
          let updatedPatient: Partial<Patient> =
            _patients.find((_pat) => _pat.patient_uid === patientId) || {};

          if (!isEmpty(updatedPatient)) {
            updatedPatient = {
              ...updatedPatient,
              emergency: !stale,
              emergency_details: newPatientEmergencyContent,
            };

            const { _allPatientsList, _emergencyPatients } = relocateEmergencyPatient(
              _patients,
              updatedPatient
            );
            _patients = _allPatientsList;
            setEmergencyPatients(_emergencyPatients);
          } else {
            getPatientDetails(patientId)
              .then((resp) => {
                updatedPatient = get(resp, ["data"], {});
                if (!isEmpty(updatedPatient)) {
                  const { _allPatientsList, _emergencyPatients } =
                    relocateEmergencyPatient(_patients, updatedPatient);
                  _patients = _allPatientsList;
                  setEmergencyPatients(_emergencyPatients);
                  const updatedPatientData = formPatientStateObject(
                    _patients,
                    _patientsData
                  );
                  setPatientsData(updatedPatientData);
                }
              })
              .catch(() => {
                Toast.error(
                  "New emergency activated for a patient. Please refresh your worklist"
                );
              });
          }
        }

        const updatedPatientData = formPatientStateObject(_patients, _patientsData);
        setPatientsData(updatedPatientData);
      }

      if (wsData.action == "update_patient") {
        const _patientsData = clone(patientsData);
        const newPatient: Patient = get(wsData, ["content"], {});
        let _patients = get(_patientsData, ["data", "content"], []);
        let patientToUpdateIdx = _patients.findIndex(
          (p) => p.patient_uid === newPatient.patient_uid
        );
        if (patientToUpdateIdx >= 0) {
          const oldPatient = _patients[patientToUpdateIdx];
          if (
            newPatient.last_image_study_uploaded_at &&
            oldPatient.last_image_study_uploaded_at !==
              newPatient.last_image_study_uploaded_at
          ) {
            _patients = _patients.filter(
              (_pat) => _pat.patient_uid !== newPatient.patient_uid
            );
            const { _emergencyPatients, _nonEmergencyPatients } =
              getPatientsByEmergency(_patients);
            _patients = [..._emergencyPatients, newPatient, ..._nonEmergencyPatients];
          } else {
            _patients[patientToUpdateIdx] = newPatient;
          }
          const updatedPatientData = formPatientStateObject(_patients, _patientsData);
          setPatientsData(updatedPatientData);
          if (currentPatient?.patient_uid === newPatient?.patient_uid) {
            setCurrentPatient(newPatient);
          }
        }
      }
      if (wsData.action === "export_download_started") {
        Store.update((s: IStore) => {
          s.showExportUpdate = true;
          s.downloadProgress = wsData.progress;
          s.exportGenFailed = false;
        });
      }
      if (wsData.action === "export_download_progress") {
        Store.update((s: IStore) => {
          s.isExportReady = false;
          s.exportGenFailed = false;
          s.downloadProgress = wsData.progress;
        });
      }
      if (wsData.action === "export_generation_complete") {
        Store.update((s: IStore) => {
          s.isExportReady = true;
          s.exportGenFailed = false;
          s.exportTimestamp = wsData.timestamp;
          s.downloadProgress = wsData.progress;
        });
        // Api call to download the Export file
        downloadFile();
        refetchProfile();
      }
      if (wsData.action === "export_download_failed") {
        Store.update((s: IStore) => {
          s.showExportUpdate = true;
          s.isExportReady = false;
          s.exportGenFailed = true;
          // s.exportTimestamp = wsData.timestamp;
          s.downloadProgress = wsData.progress;
        });
      }
      if (wsData.action === "patient_file_deletion") {
        Store.update((s: IStore) => {
          s.patientsFileDeletionStatus = wsData.status;
        });
      }
      if (wsData.action === "sync_status_live_update") {
        Store.update((s: IStore) => {
          s.worklistSyncStatus = get(wsData, ["content"], {});
        });
      }
      if (wsData.action === "sync_status_cloud_update") {
        const _syncStatus: PayloadSuccess = get(wsData, ["content"], {});
        const workspace_name = get(_syncStatus, ["workspace_name"], "");
        const kind = get(_syncStatus, ["kind"], "");
        const last_synced_at = get(_syncStatus, ["last_synced_at"], "");
        const file_sync_enabled = get(_syncStatus, ["file_sync_enabled"], false);

        if (workspace_name && kind == "success") {
          const _worklistCloudSyncStatus = cloneDeep(worklistCloudSyncStatus);
          const currentWorkspace = _worklistCloudSyncStatus[workspace_name];
          if (currentWorkspace) {
            currentWorkspace.payload = {
              kind,
              last_synced_at,
              workspace_name,
              file_sync_enabled,
            };
            _worklistCloudSyncStatus[workspace_name] = currentWorkspace;

            Store.update((s: IStore) => {
              s.worklistCloudSyncStatus = _worklistCloudSyncStatus;
            });
          }
        }
      }

      if (wsData.action === "new_workspace_referral") {
        setPendingReferral([...pendingReferral, get(wsData, ["content", "details"], {})]);
      }

      if (wsData.action === "workspace_referral_closed") {
        setPendingReferral(
          pendingReferral.filter(
            (referral) =>
              referral.referral_id !== get(wsData, ["content", "referral_id"], 0) &&
              wsData?.content?.status !== 0 &&
              wsData?.content?.status !== 1
          )
        );
      }

      if (wsData.action === "workspace_referral_updated") {
        setPendingRequest(
          pendingRequest.filter(
            (request) =>
              request.referral_id !== get(wsData, ["content", "referral_id"], 0) &&
              wsData?.content?.status !== 0 &&
              wsData?.content?.status !== 1
          )
        );
      }
    }
  }, [wsData]);

  return (
    <PatientsContext.Provider
      value={{
        patientsData,
        setPatientsData,
        getPatientData,
        getPatientCount,
        updatePatientUnreadCount,
        updatedMessage,
        setUpdatedMessage,
        unreadMessages,
        setUnreadMessages,
        currentPatient,
        setCurrentPatient,
        getFilterParams,
        lastId,
        setLastId,
        isPatientsLoading,
        setPatientsLoading,
        filters,
        setFilters,
        count,
        // unsyncCount,
        isCountLoading,
        setIsFullWidthWorklist,
        isFullWidthWorklist,
        matchingPatients,
        setMatchingPatients,
        savedFilterData,
        setSavedFilterData,
        selectedWorkspace,
        setSelectedWorkspace,
        socket,
        wsData,
        pendingReferral,
        pendingRequest,
        userCustomFields,
        refetchUserCustomFields,
        hasSyncEnabled,
        hasFileSyncEnabled,
      }}
    >
      {children}
    </PatientsContext.Provider>
  );
};
